import { Component, OnInit, OnDestroy, inject, HostListener } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subscription, map } from 'rxjs';
import { CapabilitiesCacheService } from '@luzula/luzula-lib';
import { ListenService, BodensteckbriefEntry } from './listen.service';
import { NamensmatrixService } from './namensmatrix.service';
import { AuthService } from '../auth.service';
import { canAccessListenverwaltung } from '../role-model';

/** Sortierbare Spalten der Uebersichtsliste. */
type SortKey = 'abkuerzung' | 'name' | 'klassifikation' | 'substratgruppe' | 'seitenzahl' | 'fbf';

/**
 * SEA95-Bodenformen-Katalog (Read-only-Reader).
 *
 * Liest die Steckbriefe aus der zentralen Liste `list_Bodensteckbriefe`
 * (gleicher Datentopf wie Editor/Preview) und zeigt sie blaetterbar mit Suche
 * an. Die eigentliche Anzeige delegiert er an die Bodensteckbrief-Preview-
 * Komponente (embedded-Modus, keine eigene Toolbar dort).
 *
 * Routing:
 *   /bodenformenkatalog            -> Uebersichtsliste (sortierbar) aller Steckbriefe
 *   /bodenformenkatalog?search=x   -> springt direkt auf den ersten Treffer (Nachschlagen)
 *   /bodenformenkatalog/:id        -> zeigt diesen Steckbrief
 *
 * Wichtig: Prev/Next-Navigation verwendet `replaceUrl: true`, damit der
 * Browser-Back-Button auf den urspruenglichen Aufrufer (z.B. das HBF-Feld
 * im Bohrpunkt-Editor) zurueckspringt, NICHT durch die Katalog-Seiten hindurch.
 * Genau so vom User gewuenscht: "damit beim zurueckgehen wieder der
 * ausgangspunkt erreicht wird".
 */
@Component({
  standalone: false,
  selector: 'app-bodenformenkatalog-reader',
  templateUrl: './bodenformenkatalog-reader.component.html',
  styleUrls: ['./bodenformenkatalog-reader.component.css'],
})
export class BodenformenkatalogReaderComponent implements OnInit, OnDestroy {
  private listenService = inject(ListenService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private location = inject(Location);
  private namensmatrixService = inject(NamensmatrixService);
  private capabilities = inject(CapabilitiesCacheService);
  private auth = inject(AuthService);

  /** Darf der aktuelle Nutzer den Katalog bearbeiten? Identische Single-Source-
   *  Regel (role-model.ts) wie der Editor-Route-Guard `listenverwaltungGuard`
   *  und der Navbar-Eintrag: Admin ODER Entwickler ODER Manager. Steuert nur die
   *  Button-Sichtbarkeit; die Editor-Route bleibt zusätzlich per Guard geschützt. */
  canEdit$: Observable<boolean> = this.capabilities.canManage$.pipe(
    map(admin => canAccessListenverwaltung(admin, r => this.auth.canIbe(r))),
  );

  loading = true;
  entries: BodensteckbriefEntry[] = [];
  searchTerm = '';
  /** Direkt-Filter aus den Header-Dropdowns (exakter Gleichheits-Filter,
   *  UND-verknuepft mit der Suche und untereinander). '' = kein Filter aktiv. */
  filterSubstratgruppe = '';
  filterKlassifikation = '';
  /** Auswahlwerte der Dropdowns - distinct + alphabetisch aus den Eintraegen. */
  substratgruppeOptions: string[] = [];
  klassifikationOptions: string[] = [];
  /** Eintraege in Anzeige-Reihenfolge (gefiltert + sortiert) - Quelle der
   *  Uebersichtstabelle (Listen-Modus). */
  filteredEntries: BodensteckbriefEntry[] = [];
  /** IDs in Anzeige-Reihenfolge (gefiltert + sortiert). Quelle fuer Prev/Next. */
  filteredIds: string[] = [];
  currentId = '';
  showOriginal = false;

  /** Sortierung der Uebersichtsliste. Default = Seitenzahl aufsteigend
   *  (Katalog-Reihenfolge wie im gedruckten SEA Teil D). */
  sortKey: SortKey = 'seitenzahl';
  sortDir: 'asc' | 'desc' = 'asc';

  private sub?: Subscription;

  async ngOnInit(): Promise<void> {
    const list = (await this.listenService.getList('Bodensteckbriefe')) as BodensteckbriefEntry[] | null;
    this.entries = list || [];
    // Dropdown-Auswahlwerte aus den tatsaechlich vorhandenen Eintraegen ableiten.
    this.substratgruppeOptions = this.distinctSorted(this.entries.map(e => e.hauptbodenform?.substratgruppe));
    this.klassifikationOptions = this.distinctSorted(this.entries.map(e => e.klassifikation));
    // Vorab Query-Param `?search=...` einlesen, damit der initiale Filter
    // vor dem ersten recomputeFiltered greift. Wird vom (i)-Icon in der
    // Listenverwaltung gesetzt, um den Reader auf einen HBF/FBF zu fokussieren,
    // ohne dass wir den exakten Steckbrief-Identifier wissen muessen.
    const initialSearch = this.route.snapshot.queryParamMap.get('search');
    if (initialSearch) this.searchTerm = initialSearch;
    this.recomputeFiltered();
    this.sub = this.route.paramMap.subscribe(pm => {
      const id = pm.get('id') || '';
      if (id) {
        this.currentId = id;
        // Wenn die ID nicht im aktuellen Filter ist, den Filter zuruecksetzen,
        // damit Prev/Next sinnvoll funktionieren - sonst springt der User in
        // einen "Sackgasse" ohne Nachbarn.
        if (!this.filteredIds.includes(id)) {
          this.searchTerm = '';
          this.recomputeFiltered();
        }
      } else {
        // Kein :id -> Uebersichtsliste anzeigen (statt auf den ersten Eintrag
        // zu springen). Ausnahme: ein initialer ?search=<term> (vom (i)-Icon in
        // der Listenverwaltung bzw. vom FBF-Picker zum gezielten Nachschlagen)
        // springt weiterhin direkt auf den ersten Treffer.
        this.currentId = '';
        if (initialSearch && this.filteredIds.length) {
          // replaceUrl, damit der Search-Query nicht in der History bleibt
          // (sonst spuelt Browser-Back ueber denselben Redirect mehrfach durch).
          this.router.navigate(['/bodenformenkatalog', this.bestJumpTargetId()], { replaceUrl: true });
        }
      }
    });
    this.loading = false;
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  // ========== Derived ==========

  get current(): BodensteckbriefEntry | null {
    return this.entries.find(e => e.id === this.currentId) || null;
  }
  /** Kopfzeile des geoeffneten Steckbriefs (HBF-Abk./Name/Substratgruppe) fuer
   *  die Toolbar. Dieselbe Ueberschrift steht im Steckbrief selbst, scrollt dort
   *  aber weg - die Toolbar scrollt nicht, dort bleibt sie stehen.
   *  null im Listen-Modus bzw. wenn die ID nicht (mehr) im Katalog steht. */
  get currentHeading(): { abkuerzung: string; name: string; substratgruppe: string } | null {
    const e = this.current;
    if (!e) return null;
    return {
      abkuerzung: e.hauptbodenform?.abkuerzung || '–',
      name: e.hauptbodenform?.name || '(ohne Name)',
      substratgruppe: e.hauptbodenform?.substratgruppe || '',
    };
  }
  get position(): string {
    const i = this.filteredIds.indexOf(this.currentId);
    return i >= 0 ? `${i + 1} / ${this.filteredIds.length}` : '';
  }
  get hasPrev(): boolean {
    const i = this.filteredIds.indexOf(this.currentId);
    return i > 0;
  }
  get hasNext(): boolean {
    const i = this.filteredIds.indexOf(this.currentId);
    return i >= 0 && i < this.filteredIds.length - 1;
  }

  // ========== Search / Browse ==========

  applySearch(): void {
    this.recomputeFiltered();
    // Nur im Detail-Modus auf den ersten Treffer umlenken, wenn der aktive
    // Eintrag aus dem Filter faellt (replaceUrl, damit nicht jeder Tastenanschlag
    // in der History landet). Im Listen-Modus (kein currentId) bleibt der Filter
    // einfach in der Tabelle stehen.
    if (this.currentId && this.filteredIds.length && !this.filteredIds.includes(this.currentId)) {
      this.router.navigate(['/bodenformenkatalog', this.bestJumpTargetId()], { replaceUrl: true });
    }
  }

  /**
   * Bestes Sprungziel fuer einen `?search=<HBF>`-Aufruf (i-Icon im Protokoll,
   * FBF-Picker, Handbuch). Diese Aufrufer uebergeben eine EXAKTE HBF-Abkuerzung
   * (z.B. "s GB"). Der reine Filter-Erstreffer (`filteredIds[0]`, nach Seitenzahl
   * sortiert) kann aber ein spaltenuebergreifender FBF-Teiltreffer sein, der
   * frueher einsortiert ist: "s GB" -> Kompakt "sgb" steckt als Teilstring im
   * FBF-Code "SgbS" (FBF-Abk "Sg" + Klassensuffix "bS") des Steckbriefs "bs B"
   * (Seite 114 < 258) -> der Sprung landete auf der falschen Seite.
   *
   * Darum zuerst einen Steckbrief bevorzugen, dessen HBF-Abkuerzung dem
   * Suchbegriff EXAKT (leerzeichen-/gross-klein-tolerant) entspricht; gibt es
   * keinen, bleibt es beim bisherigen Erstreffer. `filteredEntries` ist bereits
   * sortiert, `find` liefert also den ersten passenden in Anzeige-Reihenfolge
   * (z.B. Teil 1/2 vor Teil 2/2).
   */
  private bestJumpTargetId(): string {
    const compact = (s?: string) => (s || '').toLowerCase().replace(/\s+/g, '');
    const wanted = compact(this.searchTerm);
    if (wanted) {
      const exact = this.filteredEntries.find(e => compact(e.hauptbodenform?.abkuerzung) === wanted);
      if (exact) return exact.id;
    }
    return this.filteredIds[0];
  }

  private recomputeFiltered(): void {
    const term = (this.searchTerm || '').toLowerCase().trim();
    // Derselbe Begriff ohne Leerzeichen - fuer leerzeichen-tolerante Treffer.
    const termCompact = term.replace(/\s+/g, '');
    let arr = this.entries;
    // Dropdown-Direktfilter (exakte Kategorie) zuerst - UND-verknuepft mit Suche.
    if (this.filterSubstratgruppe) {
      arr = arr.filter(e => (e.hauptbodenform?.substratgruppe || '') === this.filterSubstratgruppe);
    }
    if (this.filterKlassifikation) {
      arr = arr.filter(e => (e.klassifikation || '') === this.filterKlassifikation);
    }
    if (term) {
      arr = arr.filter(e => this.matchesSearch(e, term, termCompact));
    }
    arr = [...arr].sort((a, b) => this.compareEntries(a, b));
    this.filteredEntries = arr;
    this.filteredIds = arr.map(e => e.id);
  }

  /**
   * Volltext-Treffer fuer einen Eintrag.
   *  - `term`: klassischer Substring-Treffer in einem einzelnen Feld
   *    (HBF-Abk./Name, Substratgruppe, Klassifikation, FBF-Lokalname/-Abkuerzung,
   *    Forstbetrieb).
   *  - `termCompact`: Suchbegriff ohne Leerzeichen; matcht zusaetzlich
   *    leerzeichen-tolerant und SPALTENUEBERGREIFEND. Hintergrund: Sowohl die
   *    HBF-Abkuerzung ("<praefix> <suffix>", z.B. "s B") als auch der volle
   *    FBF-Code setzen sich erst aus mehreren Feldern zusammen. Der FBF-Code
   *    ist FBF-Abkuerzung + Klassensuffix des Steckbriefs (`fbf_klassensuffix`),
   *    z.B. "Ft"+"S"="FtS" (Finowtaler) oder "Me"+"SE"="MeSE" (Meiersberger,
   *    angezeigt als "Me SE"). Achtung: der Suffix ist NICHT die Substratgruppe
   *    (die ist bei "MeSE" nur "S"). Als zusammenhaengender String existiert der
   *    Code in keinem einzelnen Feld, die reine Pro-Feld-Suche findet ihn nie.
   */
  private matchesSearch(e: BodensteckbriefEntry, term: string, termCompact: string): boolean {
    const fields: (string | undefined)[] = [
      e.hauptbodenform?.abkuerzung,
      e.hauptbodenform?.name,
      e.hauptbodenform?.substratgruppe,
      e.klassifikation,
    ];
    for (const f of e.feinbodenformen || []) {
      fields.push(f.lokalname, f.abkuerzung, f.forstbetrieb);
    }
    if (fields.some(f => (f || '').toLowerCase().includes(term))) return true;

    if (termCompact) {
      const compact = (s?: string) => (s || '').toLowerCase().replace(/\s+/g, '');
      // HBF-Abkuerzung leerzeichen-tolerant (z.B. "sB" -> "s B").
      const tokens: string[] = [compact(e.hauptbodenform?.abkuerzung)];
      // Voller FBF-Code = FBF-Abkuerzung + Klassensuffix des Steckbriefs.
      const suffix = compact(e.fbf_klassensuffix);
      for (const f of e.feinbodenformen || []) {
        const fa = compact(f.abkuerzung);
        if (fa) tokens.push(fa + suffix);
      }
      if (tokens.some(t => t && t.includes(termCompact))) return true;
    }
    return false;
  }

  // ========== Sortierung (Uebersichtsliste) ==========

  /** Spaltenkopf-Klick: gleiche Spalte -> Richtung umdrehen, sonst neue Spalte
   *  aufsteigend. */
  setSort(key: SortKey): void {
    if (this.sortKey === key) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortKey = key;
      this.sortDir = 'asc';
    }
    this.recomputeFiltered();
  }

  /** Bootstrap-Icon-Klasse fuer den Sortierindikator einer Spalte. */
  sortIcon(key: SortKey): string {
    if (this.sortKey !== key) return 'bi-arrow-down-up';
    return this.sortDir === 'asc' ? 'bi-caret-down-fill' : 'bi-caret-up-fill';
  }

  /** Aus der Uebersichtsliste in den Detail-Steckbrief springen. Normaler Push
   *  (kein replaceUrl), damit Browser-Back wieder auf die Liste zurueckfuehrt. */
  openEntry(id: string): void {
    this.router.navigate(['/bodenformenkatalog', id]);
  }

  /** Vergleicht zwei Eintraege nach aktiver Sortierspalte/-richtung. Die
   *  Richtung wirkt nur auf die Primaerspalte; bei Gleichstand wird stabil nach
   *  HBF-Abkuerzung + Name aufgeloest. */
  private compareEntries(a: BodensteckbriefEntry, b: BodensteckbriefEntry): number {
    const key: SortKey = this.sortKey || 'abkuerzung';
    const dir = this.sortDir === 'desc' ? -1 : 1;
    let cmp = this.primaryCompare(a, b, key) * dir;
    if (cmp === 0) {
      cmp = (a.hauptbodenform?.abkuerzung || '').localeCompare(b.hauptbodenform?.abkuerzung || '', 'de') ||
        (a.hauptbodenform?.name || '').localeCompare(b.hauptbodenform?.name || '', 'de');
    }
    return cmp;
  }

  private primaryCompare(a: BodensteckbriefEntry, b: BodensteckbriefEntry, key: SortKey): number {
    if (key === 'seitenzahl') {
      return this.numCompare(parseInt(a.seitenzahl ?? '', 10), parseInt(b.seitenzahl ?? '', 10));
    }
    if (key === 'fbf') {
      return (a.feinbodenformen?.length || 0) - (b.feinbodenformen?.length || 0);
    }
    return this.textValue(a, key).localeCompare(this.textValue(b, key), 'de');
  }

  private textValue(e: BodensteckbriefEntry, key: SortKey): string {
    switch (key) {
      case 'name': return e.hauptbodenform?.name || '';
      case 'klassifikation': return e.klassifikation || '';
      case 'substratgruppe': return e.hauptbodenform?.substratgruppe || '';
      case 'abkuerzung':
      default: return e.hauptbodenform?.abkuerzung || '';
    }
  }

  /** Distinct + alphabetisch (deutsch), leere Werte raus - fuer Dropdown-Optionen. */
  private distinctSorted(values: (string | undefined)[]): string[] {
    const set = new Set<string>();
    for (const v of values) {
      const s = (v || '').trim();
      if (s) set.add(s);
    }
    return [...set].sort((a, b) => a.localeCompare(b, 'de'));
  }

  /** Numerischer Vergleich; leere/ungueltige Werte landen hinter gueltigen. */
  private numCompare(an: number, bn: number): number {
    const av = !Number.isNaN(an);
    const bv = !Number.isNaN(bn);
    if (av && bv) return an - bn;
    if (av) return -1;
    if (bv) return 1;
    return 0;
  }

  prev(): void {
    const i = this.filteredIds.indexOf(this.currentId);
    if (i > 0) {
      this.router.navigate(['/bodenformenkatalog', this.filteredIds[i - 1]], { replaceUrl: true });
    }
  }
  next(): void {
    const i = this.filteredIds.indexOf(this.currentId);
    if (i >= 0 && i < this.filteredIds.length - 1) {
      this.router.navigate(['/bodenformenkatalog', this.filteredIds[i + 1]], { replaceUrl: true });
    }
  }
  toggleOriginal(): void { this.showOriginal = !this.showOriginal; }

  /** Namensmatrix (Kürzel × Substrat) als Modal öffnen. Ein Klick auf eine
   *  Treffer-Zelle navigiert auf den zugehörigen Steckbrief (HBF-Seite). */
  openNamensmatrix(): void {
    this.namensmatrixService.open();
  }

  /** Den aktuell angezeigten Steckbrief im Editor öffnen (Wechsel Viewer→Editor).
   *  Button nur für Berechtigte sichtbar (canEdit$); die Editor-Route ist zudem
   *  per Guard geschützt. Commands-Array (nicht navigateByUrl), damit der Router
   *  die '#'-haltige Steckbrief-ID korrekt kodiert. */
  editCurrent(): void {
    if (!this.currentId) return;
    this.router.navigate(['/listenverwaltung/steckbrief', this.currentId, 'edit']);
  }

  /** Zurück zum Aufrufer. Kam dieser Steckbrief aus einem Namensmatrix-Zellklick,
   *  öffnet „Zurück" zuerst wieder die Matrix (mit gleicher Sicht) statt zu
   *  blättern. Sonst: da Prev/Next intern replaceUrl nutzen, liegt im
   *  History-Stack direkt der ursprüngliche Aufrufer (Handbuch, Listenverwaltung
   *  oder die Protokoll-Eingabe) - location.back() springt also dorthin, nicht
   *  durch besuchte Katalog-Seiten. */
  back(): void {
    if (this.namensmatrixService.reopenIfFromCell()) return;
    this.location.back();
  }

  /** Komfort-Tastatur: Pfeiltasten blaettern (wenn Fokus nicht im Suchfeld). */
  @HostListener('window:keydown', ['$event'])
  onKey(ev: KeyboardEvent): void {
    if (ev.defaultPrevented) return;
    const t = ev.target as HTMLElement | null;
    // Nicht greifen wenn der User gerade in einem Input/Textarea tippt
    if (t && /^(INPUT|TEXTAREA|SELECT)$/i.test(t.tagName)) return;
    if (ev.key === 'ArrowLeft') { this.prev(); ev.preventDefault(); }
    else if (ev.key === 'ArrowRight') { this.next(); ev.preventDefault(); }
  }
}
